import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sparkles, TrendingUp, LineChart, Target, RefreshCw, ArrowUp, ArrowDown, Info } from "lucide-react";
import { toast } from "sonner";

function StatChip({ label, value, tone = "slate" }) {
  const tones = {
    slate: "bg-slate-100 text-slate-800",
    blue: "bg-blue-50 text-blue-800",
    emerald: "bg-emerald-50 text-emerald-800",
    amber: "bg-amber-50 text-amber-800",
    red: "bg-red-50 text-red-800",
  };
  return (
    <div className={`px-3 py-2 rounded-sm ${tones[tone] || tones.slate}`} data-testid={`stat-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="text-[10px] uppercase font-bold tracking-wider opacity-75">{label}</div>
      <div className="hw-head font-black text-lg">{value}</div>
    </div>
  );
}

function OccupancySection({ hotelId, refreshKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setLoading(true);
    api.get(`/ai/occupancy-forecast?hotel_id=${hotelId}`).then(({ data }) => setData(data)).finally(() => setLoading(false));
  }, [hotelId, refreshKey]);
  if (loading || !data) return <div className="p-8 text-center hw-card"><span className="hw-pulse-dot" /><p className="text-sm text-slate-500 mt-2">AI is thinking…</p></div>;
  const maxPct = Math.max(...data.forecast.map((f) => f.occupancy_pct), 1);
  return (
    <div className="hw-card p-6" data-testid="ai-occupancy-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="hw-label text-blue-700">14-day forecast</div>
          <h3 className="hw-head text-xl font-black text-slate-900 mt-1">Occupancy outlook</h3>
        </div>
        {data.fallback && <span className="text-[11px] font-bold text-amber-800 bg-amber-100 px-2 py-1 rounded-sm">Heuristic (no LLM)</span>}
      </div>
      <p className="text-sm text-slate-700 bg-blue-50 border border-blue-200 rounded-sm p-3 mt-4" data-testid="ai-occupancy-insight">
        <Info className="w-3.5 h-3.5 inline mr-1 text-blue-700" />
        {data.insight}
      </p>
      <div className="mt-5 overflow-x-auto">
        <div className="flex items-end gap-2 h-48 min-w-[560px]" data-testid="ai-occupancy-bars">
          {data.forecast.map((f) => {
            const h = Math.round((f.occupancy_pct / maxPct) * 130); // px height, max 130
            const tone = f.demand_label === "high" ? "bg-emerald-600" : f.demand_label === "medium" ? "bg-blue-600" : "bg-slate-400";
            return (
              <div key={f.date} className="flex-1 flex flex-col items-center gap-1 min-w-[24px]">
                <div className="text-[10px] font-bold text-slate-700">{f.occupancy_pct}%</div>
                <div className={`w-full ${tone} rounded-sm transition-all`} style={{ height: `${Math.max(8, h)}px` }} />
                <div className="text-[9px] text-slate-500 uppercase tracking-wider">{new Date(f.date).toLocaleDateString("en-IN", { weekday: "short" })}</div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="mt-5 flex flex-wrap gap-2">
        <StatChip label="Bookings 30d" value={data.context?.bookings_count ?? 0} tone="blue" />
        <StatChip label="Accept rate" value={`${data.context?.accept_rate_pct ?? 0}%`} tone="emerald" />
        <StatChip label="Avg discount" value={`${data.context?.avg_discount_pct ?? 0}%`} tone="amber" />
      </div>
    </div>
  );
}

function PricingSection({ hotelId, refreshKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setLoading(true);
    api.get(`/ai/price-optimizer?hotel_id=${hotelId}`).then(({ data }) => setData(data)).finally(() => setLoading(false));
  }, [hotelId, refreshKey]);
  if (loading || !data) return <div className="p-8 text-center hw-card"><span className="hw-pulse-dot" /><p className="text-sm text-slate-500 mt-2">AI is pricing your rooms…</p></div>;
  return (
    <div className="hw-card p-6" data-testid="ai-pricing-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="hw-label text-blue-700">Next 7 days</div>
          <h3 className="hw-head text-xl font-black text-slate-900 mt-1">Recommended prices</h3>
        </div>
        {data.fallback && <span className="text-[11px] font-bold text-amber-800 bg-amber-100 px-2 py-1 rounded-sm">Heuristic (no LLM)</span>}
      </div>
      <p className="text-sm text-slate-700 bg-emerald-50 border border-emerald-200 rounded-sm p-3 mt-4" data-testid="ai-pricing-summary">
        <Sparkles className="w-3.5 h-3.5 inline mr-1 text-emerald-700" />
        {data.summary}
      </p>
      <div className="mt-5 space-y-3" data-testid="ai-pricing-rooms">
        {data.rooms.map((r) => {
          const up = r.delta_pct >= 0;
          return (
            <div key={r.type} className="border border-slate-200 rounded-sm p-4 grid md:grid-cols-12 gap-3 items-start" data-testid={`ai-price-${r.type}`}>
              <div className="md:col-span-4">
                <div className="hw-head font-bold text-slate-900">{r.type}</div>
                <div className="text-xs text-slate-500">Current ₹{r.current?.toLocaleString("en-IN")}</div>
              </div>
              <div className="md:col-span-3 flex items-baseline gap-2">
                <div className="hw-head text-2xl font-black text-blue-700">₹{r.suggested?.toLocaleString("en-IN")}</div>
                <div className={`text-xs font-bold flex items-center ${up ? "text-emerald-700" : "text-red-700"}`}>
                  {up ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}{Math.abs(r.delta_pct)}%
                </div>
              </div>
              <div className="md:col-span-5 text-sm text-slate-700 leading-relaxed">{r.rationale}</div>
            </div>
          );
        })}
      </div>
      <Link to="/partner/rooms" className="mt-5 inline-flex items-center gap-1.5 text-blue-700 font-bold text-sm hover:text-blue-800" data-testid="ai-pricing-apply-link">
        <TrendingUp className="w-4 h-4" /> Apply in Rooms →
      </Link>
    </div>
  );
}

function StrategySection({ hotelId, refreshKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setLoading(true);
    api.get(`/ai/business-strategy?hotel_id=${hotelId}`).then(({ data }) => setData(data)).finally(() => setLoading(false));
  }, [hotelId, refreshKey]);
  if (loading || !data) return <div className="p-8 text-center hw-card"><span className="hw-pulse-dot" /><p className="text-sm text-slate-500 mt-2">AI is strategising…</p></div>;
  const priColor = { high: "border-red-500 bg-red-50 text-red-800", medium: "border-amber-500 bg-amber-50 text-amber-800", low: "border-slate-400 bg-slate-50 text-slate-700" };
  return (
    <div className="hw-card p-6" data-testid="ai-strategy-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="hw-label text-blue-700">This week</div>
          <h3 className="hw-head text-xl font-black text-slate-900 mt-1">Action plan</h3>
        </div>
        {data.fallback && <span className="text-[11px] font-bold text-amber-800 bg-amber-100 px-2 py-1 rounded-sm">Heuristic (no LLM)</span>}
      </div>
      <p className="hw-head text-lg font-black text-slate-900 mt-3" data-testid="ai-strategy-headline">{data.headline}</p>
      <ol className="mt-5 space-y-4" data-testid="ai-strategy-actions">
        {data.actions.map((a, i) => (
          <li key={i} className="border-l-4 border-slate-200 pl-4" data-testid={`ai-action-${i}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm border ${priColor[a.priority] || priColor.low}`}>{a.priority}</span>
              <span className="text-[10px] font-bold uppercase text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded-sm">{a.impact}</span>
              <span className="hw-head font-black text-slate-900">{a.title}</span>
            </div>
            <div className="text-sm text-slate-600 mt-1"><strong>Why:</strong> {a.why}</div>
            <div className="text-sm text-slate-700 mt-1"><strong>How:</strong> {a.how}</div>
          </li>
        ))}
      </ol>
    </div>
  );
}

export default function PartnerAIStudio() {
  const [params] = useSearchParams();
  const initialHotel = params.get("hotel") || "";
  const [hotels, setHotels] = useState([]);
  const [hotelId, setHotelId] = useState(initialHotel);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    api.get("/hotels?mine=1").then(({ data }) => {
      setHotels(data);
      if (!initialHotel && data.length) setHotelId(data[0].hotel_id);
    });
  }, [initialHotel]);

  const refreshAll = () => {
    setRefreshKey((k) => k + 1);
    toast.success("Refreshing AI insights…");
  };

  if (!hotelId) return (
    <div className="p-10 text-center">
      <p className="text-slate-600">Register or select a hotel to open AI Studio.</p>
      <Button asChild className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-bold">
        <Link to="/partner/profile">Set up hotel →</Link>
      </Button>
    </div>
  );

  return (
    <div className="bg-slate-50 min-h-[calc(100vh-4rem)]">
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-10">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="hw-label text-blue-700 flex items-center gap-1"><Sparkles className="w-3 h-3" /> AI Studio</div>
            <h1 className="hw-head text-4xl md:text-5xl font-black text-slate-900 mt-3">Grow smarter, not harder</h1>
            <p className="text-slate-600 mt-2">Occupancy forecasts, pricing recommendations & this week&apos;s playbook — for your hotel, powered by Claude.</p>
          </div>
          <Button onClick={refreshAll} variant="outline" className="rounded-sm border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white" data-testid="ai-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
        </div>

        {hotels.length > 1 && (
          <div className="mt-6 flex items-center gap-3">
            <label className="hw-label text-slate-500">Hotel</label>
            <select
              value={hotelId}
              onChange={(e) => setHotelId(e.target.value)}
              className="border border-slate-300 rounded-sm px-3 py-2 text-sm font-semibold text-slate-900"
              data-testid="ai-hotel-select"
            >
              {hotels.map((h) => <option key={h.hotel_id} value={h.hotel_id}>{h.name} · {h.city}</option>)}
            </select>
          </div>
        )}

        <Tabs defaultValue="occupancy" className="mt-8">
          <TabsList className="bg-white border border-slate-200 rounded-sm p-1">
            <TabsTrigger value="occupancy" className="rounded-sm data-[state=active]:bg-blue-700 data-[state=active]:text-white" data-testid="ai-tab-occupancy"><LineChart className="w-3.5 h-3.5 mr-1.5" />Occupancy</TabsTrigger>
            <TabsTrigger value="pricing" className="rounded-sm data-[state=active]:bg-blue-700 data-[state=active]:text-white" data-testid="ai-tab-pricing"><TrendingUp className="w-3.5 h-3.5 mr-1.5" />Pricing</TabsTrigger>
            <TabsTrigger value="strategy" className="rounded-sm data-[state=active]:bg-blue-700 data-[state=active]:text-white" data-testid="ai-tab-strategy"><Target className="w-3.5 h-3.5 mr-1.5" />Strategy</TabsTrigger>
          </TabsList>
          <TabsContent value="occupancy" className="mt-5"><OccupancySection hotelId={hotelId} refreshKey={refreshKey} /></TabsContent>
          <TabsContent value="pricing" className="mt-5"><PricingSection hotelId={hotelId} refreshKey={refreshKey} /></TabsContent>
          <TabsContent value="strategy" className="mt-5"><StrategySection hotelId={hotelId} refreshKey={refreshKey} /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
